// frontend/src/components/ProductManager.jsx
import React, { useEffect, useState, useCallback } from "react";

export default function ProductManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);

  // new product form
  const [newProd, setNewProd] = useState({ name: "", description: "", price: "", price2: "" });

  // banner/toast
  const [msg, setMsg] = useState(null); // { text, error? }

  // load templates (once)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/service-reports/templates");
        const tpls = await r.json();
        setTemplates(Array.isArray(tpls) ? tpls : []);
      } catch {}
    })();
  }, []);

  const setField = (objSetter) => (key, value) =>
    objSetter((prev) => ({ ...prev, [key]: value }));
  const setNewField = setField(setNewProd);

  // money helpers (2dp)
  const toMoneyStr = (v) => {
    if (v === "" || v === null || v === undefined) return "";
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? n.toFixed(2) : "";
  };
  const toNumberOr0 = (v) => {
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };

  // fetch list
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/products");
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      const rows = Array.isArray(data) ? data : data.data || [];
      setProducts(
        rows.map((r) => ({
          ...r,
          price: toMoneyStr(r.price),
          price2: toMoneyStr(r.price2),
        }))
      );
    } catch (e) {
      setMsg({ text: `Failed to load products: ${e.message || e}`, error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // local edit handlers
  function onEditChange(id, key, value) {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [key]: value } : p))
    );
  }

  async function addProduct() {
    if (!newProd.name.trim()) {
      setMsg({ text: "Please enter a product name.", error: true });
      return;
    }
    try {
      const body = {
        name: newProd.name.trim(),
        description: newProd.description.trim(),
        price: toNumberOr0(newProd.price),
        price2: toNumberOr0(newProd.price2),
        checklist_template_id: newProd.checklist_template_id ? Number(newProd.checklist_template_id) : null,
      };
      const r = await fetch("/api/products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      setMsg({ text: "Product added." });
      setNewProd({ name: "", description: "", price: "", price2: "" });
      fetchProducts();
    } catch (e) {
      setMsg({ text: `Add failed: ${e.message || e}`, error: true });
    }
  }

  async function saveProduct(p) {
    try {
      const body = {
        name: String(p.name || "").trim(),
        description: String(p.description || "").trim(),
        price: toNumberOr0(p.price),
        price2: toNumberOr0(p.price2),
        checklist_template_id: p.checklist_template_id ? Number(p.checklist_template_id) : null,
      };
      const r = await fetch(`/api/products/${p.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      setMsg({ text: "Product saved." });
      fetchProducts();
    } catch (e) {
      setMsg({ text: `Save failed: ${e.message || e}`, error: true });
    }
  }

  async function deleteProduct(id) {
    const sure = window.confirm("Delete this product? This cannot be undone.");
    if (!sure) return;
    try {
      const r = await fetch(`/api/products/${id}`, { method: "DELETE" });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      setMsg({ text: "Product deleted." });
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setMsg({ text: `Delete failed: ${e.message || e}`, error: true });
    }
  }

  const labelS = { fontSize: 12, fontWeight: 600, marginBottom: 4 };

  return (
    <div className="container product-manager">
      {/* Scoped styles for ProductManager */}
      <style>{`
        .product-manager {
          --pm-blue: #121A2B; /* dark blue */
          --pm-lblue: #22314a;
        }

        .pm-row, .pm-add {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: flex-start;
        }
        /* Field sizing similar to QuoteBuilder */
        .pm-name   { flex: 1 1 260px; }
        .pm-desc   { flex: 1 1 320px; }
        .pm-price  { flex: 0 0 120px; }
        .pm-price2 { flex: 0 0 120px; }
        .pm-actions{ flex: 0 0 auto; margin-left: auto; }

        /* On tablets/desktops, keep everything on one line neatly */
        @media (min-width: 768px) {
          .pm-row > div, .pm-add > div { align-self: center; }
        }

        /* === Select styling (closed + open list) ===========================
           Note: option styling is browser-dependent; this works in modern Chromium/Firefox.
           Closed state: dark-blue bg, white text (matches your current look).
           Open list: white bg, BLUE text for each option so it's readable.
        ==================================================================== */
        .product-manager select.input {
          background: var(--pm-blue);
          color: #fff;
          border-color: var(--pm-lblue);
        }
        .product-manager select.input:focus {
          outline: 2px solid var(--pm-blue);
          outline-offset: 0;
        }
        .product-manager select.input option {
          color: var(--pm-blue);
          background: #fff;
        }
        /* Highlighted/selected option in list */
        .product-manager select.input option:checked,
        .product-manager select.input option:hover {
          background: #eaf0fb; /* pale blue */
          color: var(--pm-blue);
        }
        /* In case of optgroups */
        .product-manager select.input optgroup {
          color: var(--pm-blue);
          background: #fff;
          font-weight: 600;
        }
      `}</style>

      <div className="card">
        <h2 className="section-title" style={{ margin: 0 }}>Product Manager</h2>
        <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0 12px" }} />

        {/* Banner */}
        {msg && (
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              borderRadius: 8,
              border: `1px solid ${msg.error ? "#fecaca" : "#cce3d1"}`,
              background: msg.error ? "#fee2e2" : "#e7f6ec",
              color: msg.error ? "#991b1b" : "#135f2b",
            }}
          >
            {msg.text}
          </div>
        )}

        {/* Add new product */}
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 className="section-title" style={{ margin: 0 }}>Add Product</h3>
          <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0 12px" }} />

          <div className="pm-add">
            <div className="pm-name">
              <div style={labelS}>Name</div>
              <input
                className="input"
                placeholder="Name"
                value={newProd.name}
                onChange={(e) => setNewField("name", e.target.value)}
              />
            </div>
            <div className="pm-desc">
              <div style={labelS}>Description</div>
              <input
                className="input"
                placeholder="Description"
                value={newProd.description}
                onChange={(e) => setNewField("description", e.target.value)}
              />
            </div>
            <div className="pm-price">
              <div style={labelS}>Inv. Price</div>
              <input
                className="input"
                placeholder="Price"
                type="text"
                inputMode="decimal"
                pattern="^[0-9]*(?:[.,][0-9]{0,2})?$"
                value={newProd.price}
                onChange={(e) => setNewField("price", e.target.value)}
                onBlur={(e) => setNewField("price", toMoneyStr(e.target.value))}
              />
            </div>
            <div className="pm-price2">
              <div style={labelS}>Recpt. Price</div>
              <input
                className="input"
                placeholder="Price 2"
                type="text"
                inputMode="decimal"
                pattern="^[0-9]*(?:[.,][0-9]{0,2})?$"
                value={newProd.price2}
                onChange={(e) => setNewField("price2", e.target.value)}
                onBlur={(e) => setNewField("price2", toMoneyStr(e.target.value))}
              />
            </div>
            <div className="pm-desc">
              <div style={labelS}>Checklist Template</div>
              <select
                className="input"
                value={newProd.checklist_template_id || ""}
                onChange={(e) => setNewField("checklist_template_id", e.target.value || "")}
              >
                <option value="">— None —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            <div className="pm-actions" style={{ display: "flex", alignItems: "flex-end" }}>
              <button className="btn" onClick={addProduct}>Add</button>
            </div>
          </div>
        </div>

        {/* Product list */}
        <div className="card">
          <h3 className="section-title" style={{ margin: 0 }}>Products</h3>
          <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0 12px" }} />

          {loading ? (
            <div>Loading…</div>
          ) : products.length === 0 ? (
            <div>No products yet.</div>
          ) : (
            <div className="form-grid" style={{ gridTemplateColumns: "1fr", gap: 10 }}>
              {products.map((p) => (
                <div key={p.id} className="card" style={{ padding: 12 }}>
                  <div className="pm-row">
                    <div className="pm-name">
                      <div style={labelS}>Name</div>
                      <input
                        className="input"
                        value={p.name || ""}
                        onChange={(e) => onEditChange(p.id, "name", e.target.value)}
                      />
                    </div>
                    <div className="pm-desc">
                      <div style={labelS}>Description</div>
                      <input
                        className="input"
                        value={p.description || ""}
                        onChange={(e) => onEditChange(p.id, "description", e.target.value)}
                      />
                    </div>
                    <div className="pm-price">
                      <div style={labelS}>Inv. Price</div>
                      <input
                        className="input"
                        type="text"
                        inputMode="decimal"
                        pattern="^[0-9]*(?:[.,][0-9]{0,2})?$"
                        value={p.price ?? ""}
                        onChange={(e) => onEditChange(p.id, "price", e.target.value)}
                        onBlur={(e) => onEditChange(p.id, "price", toMoneyStr(e.target.value))}
                      />
                    </div>
                    <div className="pm-price2">
                      <div style={labelS}>Recpt. Price</div>
                      <input
                        className="input"
                        type="text"
                        inputMode="decimal"
                        pattern="^[0-9]*(?:[.,][0-9]{0,2})?$"
                        value={p.price2 ?? ""}
                        onChange={(e) => onEditChange(p.id, "price2", e.target.value)}
                        onBlur={(e) => onEditChange(p.id, "price2", toMoneyStr(e.target.value))}
                      />
                    </div>
                    <div className="pm-desc">
                      <div style={labelS}>Checklist Template</div>
                      <select
                        className="input"
                        value={p.checklist_template_id || ""}
                        onChange={(e) => onEditChange(p.id, "checklist_template_id", e.target.value || "")}
                      >
                        <option value="">— None —</option>
                        {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                    <div className="pm-actions" style={{ display: "flex", gap: 8 }}>
                      <div style={labelS}>Actions</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button className="btn" onClick={() => saveProduct(p)}>Save</button>
                        <button className="btn cancel" onClick={() => deleteProduct(p.id)}>Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
